// Globals
import { useTranslation } from 'react-i18next';

// Models
import { DocumentSourceEnum, IPackageDocumentDTO } from '@/models/documents.model';

// Components
import { Checkbox, Tooltip } from '@/ui';
import {
  StyledDivider,
  StyledTooltipContent,
  StyledTitle,
  StyledWrapper,
  StyledCheckboxesBlock,
  StyledCheckboxGroup,
  StyledCheckboxText,
  StyledCheckboxesGroup,
} from './PropertyDocumentSourceItemStyles';
import { InfoIcon } from '@/icons';

// Helpers
import { divideRequiredDocs } from '../../helpers';
import { useLocale } from '@/hooks';
import { pickThemeColor } from '@/theme';

type TPropertyDocumentSourceItem = {
  onUpdateDocumentStatus: (id: string, is_checked: boolean) => void;
  documents: IPackageDocumentDTO[];
  source: DocumentSourceEnum;
  uploadedHidden?: boolean;
  isLastOne?: boolean;
  disabled?: boolean;
};

const PropertyDocumentSourceItem = ({
  documents,
  source,
  onUpdateDocumentStatus,
  uploadedHidden,
  isLastOne,
  disabled = false,
}: TPropertyDocumentSourceItem) => {
  const { t } = useTranslation('documents');
  const { required, optional } = divideRequiredDocs(documents);
  const { isNorwegian } = useLocale();

  const renderCheckbox = (checkBox: IPackageDocumentDTO, index: number) => {
    if (uploadedHidden && checkBox.is_checked) {
      return null;
    }

    return (
      <StyledCheckboxGroup key={`${checkBox.id}-${index}`}>
        <Checkbox
          onChange={(e) => {
            onUpdateDocumentStatus(checkBox.id, e.target.checked);
          }}
          defaultChecked={checkBox.is_checked}
          disabled={disabled}
        >
          <StyledCheckboxText>
            {isNorwegian ? checkBox.name_no : checkBox.name_en}{' '}
            {!checkBox?.is_required && <span>({t('documents_upload_optional')})</span>}
          </StyledCheckboxText>
        </Checkbox>

        <Tooltip
          title={
            <StyledTooltipContent>
              {isNorwegian ? checkBox.description_no : checkBox.description_en}
            </StyledTooltipContent>
          }
          color={pickThemeColor('DARK_PURPLE')}
          overlayClassName="custom-tooltip"
        >
          <InfoIcon />
        </Tooltip>
      </StyledCheckboxGroup>
    );
  };

  const requiredCheckboxes = required?.map((doc, index) => renderCheckbox(doc, index));

  const optionalCheckboxes = optional?.map((doc, index) => renderCheckbox(doc, index));

  return (
    <>
      <StyledWrapper>
        <StyledTitle>{t(`documents_${source.toLowerCase()}_block_title`)}</StyledTitle>
        <StyledCheckboxesBlock>
          <StyledCheckboxesGroup>{requiredCheckboxes?.filter(Boolean)}</StyledCheckboxesGroup>
          <StyledCheckboxesGroup>{optionalCheckboxes?.filter(Boolean)}</StyledCheckboxesGroup>
        </StyledCheckboxesBlock>
      </StyledWrapper>
      {!isLastOne && <StyledDivider />}
    </>
  );
};

export default PropertyDocumentSourceItem;
